/**
 * Auth Service Layer
 */
import { ApiError } from '../errors/api-error.js';

export class AuthService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
    }

    _unwrap(response, fallbackMessage) {
        const payload = response.data;
        return {
            success: payload.success ?? true,
            data: payload.data ?? payload,
            message: payload.message || response.message || fallbackMessage,
        };
    }

    async createLogin(data = {}) {
        try {
            return this._unwrap(await this.api.login(data));
        } catch (error) {
            throw this._handleError(error, 'Failed to execute createLogin');
        }
    }

    async ssoExchange(code) {
        try {
            return this._unwrap(await this.api.ssoExchange(code));
        } catch (error) {
            throw this._handleError(error, 'SSO exchange failed');
        }
    }

    async listPublicSsoProviders() {
        try {
            return this._unwrap(await this.api.listPublicSsoProviders());
        } catch (error) {
            throw this._handleError(error, 'SSO providers failed');
        }
    }

    async createRegister(data = {}) {
        try {
            return this._unwrap(await this.api.register(data));
        } catch (error) {
            throw this._handleError(error, 'Failed to execute createRegister');
        }
    }

    async createRefresh(data = {}) {
        try {
            return this._unwrap(await this.api.refreshToken(data.refreshToken));
        } catch (error) {
            throw this._handleError(error, 'Failed to execute createRefresh');
        }
    }

    async createForgotPassword(data = {}) {
        try {
            return this._unwrap(await this.api.forgotPassword(data));
        } catch (error) {
            throw this._handleError(error, 'Failed to execute createForgotPassword');
        }
    }

    async verifyForgotPassword(data = {}) {
        try {
            return this._unwrap(await this.api.forgotPasswordVerify(data));
        } catch (error) {
            throw this._handleError(error, 'Failed to verify forgot password code');
        }
    }

    async createResetPassword(data = {}) {
        try {
            return this._unwrap(await this.api.resetPassword(data));
        } catch (error) {
            throw this._handleError(error, 'Failed to execute createResetPassword');
        }
    }

    async createActivateAccount(data = {}) {
        try {
            return this._unwrap(await this.api.activateAccount(data));
        } catch (error) {
            throw this._handleError(error, 'Failed to activate account');
        }
    }

    async getTenantAuthOptions(tenantId) {
        try {
            return this._unwrap(await this.api.getTenantAuthOptions(tenantId));
        } catch (error) {
            throw this._handleError(error, 'Failed to load tenant auth options');
        }
    }

    async getVerificationChannels(tenantId = null) {
        try {
            return this._unwrap(await this.api.getVerificationChannels(tenantId));
        } catch (error) {
            throw this._handleError(error, 'Failed to load verification channels');
        }
    }

    async verifyMfa(data = {}) {
        try {
            return this._unwrap(await this.api.verifyMfa(data));
        } catch (error) {
            throw this._handleError(error, 'Failed to verify MFA');
        }
    }

    async resendMfa(data = {}) {
        try {
            return this._unwrap(await this.api.resendMfa(data));
        } catch (error) {
            throw this._handleError(error, 'Failed to resend MFA code');
        }
    }

    async requestPasswordChangeOtp(data = {}) {
        try {
            return this._unwrap(await this.api.requestPasswordChangeOtp(data));
        } catch (error) {
            throw this._handleError(error, 'Failed to request password OTP');
        }
    }

    async confirmPasswordChange(data = {}) {
        try {
            return this._unwrap(await this.api.confirmPasswordChange(data));
        } catch (error) {
            throw this._handleError(error, 'Failed to change password');
        }
    }

    async changeExpiredPassword(data = {}) {
        try {
            return this._unwrap(await this.api.changeExpiredPassword(data));
        } catch (error) {
            throw this._handleError(error, 'Failed to renew expired password');
        }
    }

    async requestMfaToggleOtp(data = {}) {
        try {
            return this._unwrap(await this.api.requestMfaToggleOtp(data));
        } catch (error) {
            throw this._handleError(error, 'Failed to request MFA OTP');
        }
    }

    async confirmMfaToggle(data = {}) {
        try {
            return this._unwrap(await this.api.confirmMfaToggle(data));
        } catch (error) {
            throw this._handleError(error, 'Failed to update MFA');
        }
    }

    async patchMySecurity(data = {}) {
        try {
            return this._unwrap(await this.api.patchMySecurity(data));
        } catch (error) {
            throw this._handleError(error, 'Failed to update security settings');
        }
    }

    async evaluateTenantSwitch(data = {}) {
        try {
            return this._unwrap(await this.api.evaluateTenantSwitch(data));
        } catch (error) {
            throw this._handleError(error, 'Failed to evaluate tenant switch');
        }
    }

    async verifyTenantSwitch(data = {}) {
        try {
            return this._unwrap(await this.api.verifyTenantSwitch(data));
        } catch (error) {
            throw this._handleError(error, 'Failed to verify tenant switch');
        }
    }

    async getMe() {
        try {
            return this._unwrap(await this.api.getCurrentUser());
        } catch (error) {
            throw this._handleError(error, 'Failed to execute getMe');
        }
    }

    async getPartyAssignmentFlags() {
        try {
            return this._unwrap(await this.api.getPartyAssignmentFlags());
        } catch (error) {
            throw this._handleError(error, 'Failed to load party assignment flags');
        }
    }

    async createLogout(data = {}) {
        try {
            return this._unwrap(await this.api.logout(data.refreshToken));
        } catch (error) {
            throw this._handleError(error, 'Failed to execute createLogout');
        }
    }

    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) {
            throw error;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
